<script setup lang="ts">
import type { IChipVariant } from "../ui/Chip/chip.interface"
import type { EventVault } from "@/models"
import cover from "@brand-cover"
import { computed } from "vue"
import { Button, ButtonLink, Chip, Icon } from "@/components/ui"
import { useTranslation } from "@/composables"
import { EventVaultStatus, ProductTypes } from "@/models"
import { convertDateTimezone } from "@/utils"

const props = defineProps<{
  eventVault: EventVault
  viewOnly: boolean
}>()

const { t, d } = useTranslation()

const zipDownloadUrl = computed(() => props.eventVault.zipSummary?.downloadUrl)

const isEditDisabled = computed(() => props.eventVault.status === EventVaultStatus.Expired)

const isSubscriptionEvent = computed(() => Boolean(props.eventVault?.packageSubscriptionId))

const packageLabel = computed<string>(() => {
  return ProductTypes.getLabel(props.eventVault?.productKey, props.eventVault?.productName)
})

const statusLabel = computed<string>(() => {
  return EventVaultStatus.getLabel(props.eventVault?.status ?? EventVaultStatus.Draft)
})

const statusVariants: Record<EventVaultStatus, IChipVariant> = {
  [EventVaultStatus.Draft]: "info",
  [EventVaultStatus.Ready]: "info",
  [EventVaultStatus.Live]: "success",
  [EventVaultStatus.Closed]: "info",
  [EventVaultStatus.Expired]: "danger",
  [EventVaultStatus.Archived]: "info",
  [EventVaultStatus.Expiring]: "warning",
  [EventVaultStatus.ExtendInProgress]: "warning",
  [EventVaultStatus.DeletingInProgress]: "danger",
  [EventVaultStatus.All]: "info",
}
</script>

<template>
  <div class="flex flex-col border border-secondary rounded-xl overflow-hidden">
    <h2 class="font-comfortaa text-lg font-bold bg-tertiary p-4">
      {{ t("summary_page.details_card.title") }}
    </h2>
    <div class="flex flex-col gap-2 md:gap-4 p-4">
      <img v-if="eventVault.coverImageUrl" :src="eventVault.coverImageUrl" :alt="eventVault.title" class="w-full h-56 object-cover rounded-md">
      <img v-else :src="cover" :alt="eventVault.title" class="w-full h-56 object-cover rounded-md">
      <div class="flex items-start gap-2">
        <Icon icon="Calendar" class="shrink-0" />
        <p>{{ eventVault?.timezone ? d(convertDateTimezone(eventVault.eventDate ?? new Date(), eventVault.timezone), "long") : d(eventVault.eventDate ?? new Date(), "short") }}</p>
      </div>
      <div v-if="eventVault.location?.displayAddress" class="flex items-start gap-2">
        <Icon icon="Location" class="shrink-0" />
        <p>{{ eventVault.location?.displayAddress }}</p>
      </div>

      <div class="flex items-center gap-2 flex-wrap">
        <Chip :label="statusLabel" :variant="statusVariants[eventVault.status]" size="sm" />
        <Chip :label="packageLabel" variant="secondary" size="sm" />
      </div>

      <p v-if="!isSubscriptionEvent" class="text-sm font-medium" data-test="expiry-date">
        {{ t("summary_page.details_card.expiry", { expiry: eventVault.expiryDate ? d(eventVault.expiryDate, "short") : "N/A" }) }}
      </p>

      <a v-if="zipDownloadUrl" :href="zipDownloadUrl" target="_blank" class="text-link flex items-center gap-2" data-test="zip_download_link">
        <Icon icon="Download" />
        {{ t("summary_page.details_card.download_zip") }}
      </a>

      <Button v-if="isEditDisabled && !viewOnly" disabled variant="tertiary" prefix-icon="Edit" class="mt-4">
        {{ t("summary_page.details_card.edit_details_btn") }}
      </Button>
      <ButtonLink v-else-if="!viewOnly" :to="`/event/${eventVault.id}/edit`" variant="tertiary" prefix-icon="Edit" class="mt-4">
        {{ t("summary_page.details_card.edit_details_btn") }}
      </ButtonLink>
    </div>
  </div>
</template>
